import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Users, TrendUp, Tag, Buildings, FunnelSimple, CurrencyDollar, ArrowRight,
  Phone, EnvelopeSimple, NotePencil, VideoCamera, ListChecks, Warning, CheckCircle, ClockCounterClockwise, Sparkle } from '@phosphor-icons/react';
import DemoAccountHealthWidget from '../components/DemoAccountHealthWidget';
import SalesLeadsWidget from '../components/SalesLeadsWidget';
import ObjectionMemoryWidget from '../components/ObjectionMemoryWidget';
import MagicLinkWidget from '../components/MagicLinkWidget';

const ACTIVITY_META = {
  call:    { icon: Phone,         color: '#2563EB', bg: '#DBEAFE', label: 'Call' },
  meeting: { icon: VideoCamera,   color: '#7C3AED', bg: '#EDE9FE', label: 'Meeting' },
  todo:    { icon: ListChecks,    color: '#0B5394', bg: '#DBEAFE', label: 'To-do' },
  task:    { icon: ListChecks,    color: '#0B5394', bg: '#DBEAFE', label: 'Task' },
  email:   { icon: EnvelopeSimple,color: '#16A34A', bg: '#DCFCE7', label: 'Email' },
  note:    { icon: NotePencil,    color: '#D97706', bg: '#FEF3C7', label: 'Note' },
};

function formatTime(t) {
  if (!t) return '';
  // accept "14:30" or "2:30 PM"
  if (/^\d{2}:\d{2}/.test(t)) {
    const [h, m] = t.split(':');
    const hh = parseInt(h, 10);
    const ampm = hh >= 12 ? 'PM' : 'AM';
    const h12 = ((hh + 11) % 12) + 1;
    return `${h12}:${m} ${ampm}`;
  }
  return t;
}

function ActivitiesForToday({ onNavigateToContact }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/alarms')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const completeActivity = async (activityId) => {
    try {
      await api.put(`/activities/${activityId}/complete`);
      setData(d => ({
        ...d,
        overdue: d.overdue.filter(a => a.activity_id !== activityId),
        today_items: d.today_items.filter(a => a.activity_id !== activityId),
        overdue_count: Math.max(0, (d.overdue_count || 0) - 1),
        today_count: Math.max(0, (d.today_count || 0) - 1),
      }));
    } catch (e) { console.error(e); }
  };

  const today = new Date();
  const todayStr = today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-sm p-6">
        <div className="w-5 h-5 mx-auto border-2 border-[#0B5394] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const overdue = data?.overdue || [];
  const todayItems = data?.today_items || [];
  const allEmpty = overdue.length === 0 && todayItems.length === 0;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm mb-4" data-testid="activities-today-widget">
      <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-[#0B5394] flex items-center gap-1.5">
            <ListChecks size={18} weight="bold" />
            Activities for Today
          </h2>
          <p className="text-xs text-[#4B5563] mt-0.5">{todayStr}</p>
        </div>
        <div className="flex items-center gap-2">
          {overdue.length > 0 && (
            <span className="text-[11px] font-semibold px-2 py-1 bg-red-50 border border-red-200 text-red-700 rounded-sm flex items-center gap-1">
              <Warning size={12} weight="bold" /> {overdue.length} overdue
            </span>
          )}
          <span className="text-[11px] font-semibold px-2 py-1 bg-blue-50 border border-blue-200 text-[#0B5394] rounded-sm">
            {todayItems.length} today
          </span>
        </div>
      </div>

      {allEmpty ? (
        <div className="p-8 text-center" data-testid="activities-empty">
          <CheckCircle size={32} weight="fill" className="text-green-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-[#111827]">You&apos;re all caught up.</p>
          <p className="text-xs text-[#4B5563] mt-1">No overdue items, no calls or meetings scheduled for today.</p>
        </div>
      ) : (
        <div className="divide-y divide-[#F1F5F9]">
          {overdue.map(a => (
            <ActivityRow key={a.activity_id} activity={a} overdue onComplete={completeActivity} onClick={onNavigateToContact} />
          ))}
          {todayItems.map(a => (
            <ActivityRow key={a.activity_id} activity={a} onComplete={completeActivity} onClick={onNavigateToContact} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityRow({ activity, overdue, onComplete, onClick }) {
  const meta = ACTIVITY_META[activity.activity_type] || ACTIVITY_META.todo;
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-3 px-4 py-2 hover:bg-[#F8FAFC] group cursor-pointer" data-testid={`activity-row-${activity.activity_id}`}
      onClick={() => activity.contact_id && onClick(activity.contact_id)}>
      <button
        onClick={(e) => { e.stopPropagation(); onComplete(activity.activity_id); }}
        className="w-5 h-5 rounded-full border-2 border-[#CBD5E1] hover:border-green-500 hover:bg-green-50 flex items-center justify-center shrink-0 transition-colors"
        data-testid={`activity-complete-${activity.activity_id}`}
        title="Mark complete"
      >
        <CheckCircle size={14} weight="bold" className="text-green-500 opacity-0 group-hover:opacity-100" />
      </button>
      <div className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0" style={{ background: meta.bg, color: meta.color }}>
        <Icon size={15} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#111827] truncate">{activity.title || meta.label}</span>
          {overdue && (
            <span className="text-[10px] font-bold uppercase text-red-600 flex items-center gap-0.5">
              <ClockCounterClockwise size={11} weight="bold" /> overdue
            </span>
          )}
        </div>
        <div className="text-xs text-[#4B5563] truncate">
          {activity.contact_name}
          {activity.contact_company && <span className="text-[#9CA3AF]"> &middot; {activity.contact_company}</span>}
        </div>
      </div>
      <div className="text-xs text-[#4B5563] tabular-nums text-right shrink-0">
        {overdue ? <span className="text-red-700 font-medium">{activity.scheduled_date}</span> : (formatTime(activity.scheduled_time) || 'Anytime')}
      </div>
    </div>
  );
}

const FUNNEL_STAGES = [
  { id: 'lead', label: 'Lead', color: '#6B7280', bg: '#F3F4F6' },
  { id: 'qualified', label: 'Qualified', color: '#2563EB', bg: '#DBEAFE' },
  { id: 'proposal', label: 'Proposal', color: '#D97706', bg: '#FEF3C7' },
  { id: 'negotiation', label: 'Negotiation', color: '#7C3AED', bg: '#EDE9FE' },
  { id: 'won', label: 'Won', color: '#16A34A', bg: '#DCFCE7' },
  { id: 'lost', label: 'Lost', color: '#DC2626', bg: '#FEE2E2' },
];

function formatCurrency(val) {
  if (!val) return '$0';
  if (val >= 1000000) return '$' + (val / 1000000).toFixed(1) + 'M';
  if (val >= 1000) return '$' + (val / 1000).toFixed(0) + 'K';
  return '$' + val.toLocaleString();
}

function PipelineFunnel({ summary, onNavigate }) {
  const activeStages = FUNNEL_STAGES.filter(s => s.id !== 'lost');
  const lostStage = FUNNEL_STAGES.find(s => s.id === 'lost');

  const maxCount = Math.max(
    ...activeStages.map(s => summary[s.id]?.count || 0),
    1
  );

  const totalPipelineValue = activeStages
    .filter(s => s.id !== 'won')
    .reduce((sum, s) => sum + (summary[s.id]?.total_value || 0), 0);

  const wonValue = summary.won?.total_value || 0;
  const totalDeals = Object.values(summary).reduce((sum, s) => sum + (s?.count || 0), 0);

  if (totalDeals === 0) {
    return (
      <div className="text-center py-10">
        <FunnelSimple size={40} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm text-[#4B5563] mb-2">No deals in your pipeline yet</p>
        <button
          onClick={() => onNavigate('/pipeline')}
          className="text-sm font-medium text-[#002FA7] hover:underline"
          data-testid="funnel-empty-cta"
        >
          Create your first deal
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Pipeline summary header */}
      <div className="flex items-center gap-6 mb-5 flex-wrap">
        <div>
          <p className="text-xs text-[#4B5563] uppercase tracking-wide font-medium">In Pipeline</p>
          <p className="text-2xl font-black text-[#111827]">{formatCurrency(totalPipelineValue)}</p>
        </div>
        <div className="w-px h-8 bg-[#E5E7EB]" />
        <div>
          <p className="text-xs text-[#4B5563] uppercase tracking-wide font-medium">Won</p>
          <p className="text-2xl font-black text-[#16A34A]">{formatCurrency(wonValue)}</p>
        </div>
        <div className="w-px h-8 bg-[#E5E7EB]" />
        <div>
          <p className="text-xs text-[#4B5563] uppercase tracking-wide font-medium">Total Deals</p>
          <p className="text-2xl font-black text-[#111827]">{totalDeals}</p>
        </div>
      </div>

      {/* Funnel bars */}
      <div className="space-y-2">
        {activeStages.map((stage, idx) => {
          const data = summary[stage.id] || { count: 0, total_value: 0 };
          const widthPct = maxCount > 0 ? Math.max((data.count / maxCount) * 100, 4) : 4;
          const nextStage = activeStages[idx + 1];
          const nextData = nextStage ? (summary[nextStage.id] || { count: 0 }) : null;
          const conversionRate = nextData && data.count > 0
            ? Math.round((nextData.count / data.count) * 100)
            : null;

          return (
            <div key={stage.id} data-testid={`funnel-stage-${stage.id}`}>
              <div className="flex items-center gap-3">
                {/* Stage label */}
                <div className="w-24 flex-shrink-0 text-right">
                  <span className="text-xs font-semibold text-[#4B5563] uppercase tracking-wide">{stage.label}</span>
                </div>

                {/* Bar */}
                <div className="flex-1 relative">
                  <div
                    className="h-9 rounded-sm flex items-center px-3 transition-all duration-300 cursor-pointer hover:opacity-90"
                    style={{
                      width: `${widthPct}%`,
                      backgroundColor: stage.bg,
                      borderLeft: `3px solid ${stage.color}`,
                      minWidth: '60px',
                    }}
                    onClick={() => onNavigate('/pipeline')}
                    data-testid={`funnel-bar-${stage.id}`}
                  >
                    <span className="text-sm font-bold" style={{ color: stage.color }}>
                      {data.count}
                    </span>
                    {data.total_value > 0 && (
                      <span className="text-xs text-[#4B5563] ml-2">
                        {formatCurrency(data.total_value)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Conversion arrow */}
                <div className="w-14 flex-shrink-0 text-center">
                  {conversionRate !== null && data.count > 0 && (
                    <span className="text-[10px] font-bold text-[#4B5563]" data-testid={`funnel-conversion-${stage.id}`}>
                      {conversionRate}%
                      <ArrowRight size={9} className="inline ml-0.5" />
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Lost row (separate, muted) */}
        {(summary.lost?.count || 0) > 0 && (
          <div className="flex items-center gap-3 mt-1 opacity-60" data-testid="funnel-stage-lost">
            <div className="w-24 flex-shrink-0 text-right">
              <span className="text-xs font-semibold text-[#DC2626] uppercase tracking-wide">Lost</span>
            </div>
            <div className="flex-1">
              <div
                className="h-7 rounded-sm flex items-center px-3"
                style={{
                  width: `${Math.max((summary.lost.count / maxCount) * 100, 4)}%`,
                  backgroundColor: '#FEE2E2',
                  borderLeft: '3px solid #DC2626',
                  minWidth: '60px',
                }}
              >
                <span className="text-xs font-bold text-[#DC2626]">{summary.lost.count}</span>
                {summary.lost.total_value > 0 && (
                  <span className="text-xs text-[#4B5563] ml-2">{formatCurrency(summary.lost.total_value)}</span>
                )}
              </div>
            </div>
            <div className="w-14" />
          </div>
        )}
      </div>
    </div>
  );
}

const FUNNEL_LABELS = {
  signup: 'Signed up',
  tour_started: 'Started tour',
  tour_completed: 'Finished tour',
  first_contact_created: 'Created first contact',
  first_email_sent: 'Sent first email',
};

function ActivationFunnelWidget({ funnel }) {
  if (!funnel) return null;
  const { total_signups, stages } = funnel;
  const max = stages[0]?.count || 1;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm mb-4" data-testid="activation-funnel-widget">
      <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center gap-2">
        <Sparkle size={16} weight="fill" className="text-[#F39C12]" />
        <h3 className="text-sm font-semibold text-[#111827] uppercase tracking-wide">
          Activation Funnel
        </h3>
        <span className="text-[11px] text-[#6B7280] ml-auto">
          {total_signups.toLocaleString()} signup{total_signups === 1 ? '' : 's'} all-time
        </span>
      </div>
      <div className="p-4 space-y-2">
        {stages.map((s) => {
          const widthPct = Math.max(2, (s.count / max) * 100);
          const isFirst = s.event === 'signup';
          return (
            <div key={s.event} className="flex items-center gap-3" data-testid={`funnel-stage-${s.event}`}>
              <div className="w-44 shrink-0 text-[12px] font-medium text-[#374151]">
                {FUNNEL_LABELS[s.event] || s.event}
              </div>
              <div className="flex-1 relative h-6 bg-[#F3F4F6] rounded-sm overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#0B5394] to-[#1E6FB8] transition-all"
                  style={{ width: `${widthPct}%` }}
                />
                <span className="absolute inset-0 flex items-center px-2 text-[11px] font-bold text-white mix-blend-difference">
                  {s.count.toLocaleString()}
                </span>
              </div>
              <div className="w-16 shrink-0 text-right text-[11px] tabular-nums text-[#111827] font-semibold">
                {s.pct_of_signup}%
              </div>
              <div className="w-20 shrink-0 text-right text-[11px] tabular-nums text-[#6B7280]">
                {isFirst ? '—' : `${s.pct_from_prev}% from prev`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [funnel, setFunnel] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats'),
      api.get('/deals/summary'),
    ]).then(([statsRes, pipelineRes]) => {
      setStats(statsRes.data);
      setPipelineSummary(pipelineRes.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Admin-only: load the activation funnel
  useEffect(() => {
    if (user?.role !== 'admin') return;
    api.get('/admin/funnel/stats')
      .then(r => setFunnel(r.data))
      .catch(() => {});
  }, [user?.role]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Contacts', value: stats?.total_contacts || 0, icon: Users, color: '#002FA7' },
    { label: 'Added This Week', value: stats?.recent_additions || 0, icon: TrendUp, color: '#16A34A' },
    { label: 'Leads', value: stats?.status_breakdown?.lead || 0, icon: Tag, color: '#D97706' },
    { label: 'Customers', value: stats?.status_breakdown?.customer || 0, icon: Buildings, color: '#7C3AED' },
  ];

  return (
    <div data-testid="dashboard-page" className="p-4 sm:p-6 max-w-[1600px]">
      <div className="mb-4">
        <h1 className="text-xl sm:text-2xl font-black tracking-tight text-[#0B5394]" data-testid="dashboard-title">
          Welcome back, {user?.name?.split(' ')[0] || 'User'}
        </h1>
        <p className="text-xs text-[#4B5563] mt-0.5">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
      </div>

      {/* Activities for Today — the classic ACT! homepage view */}
      <ActivitiesForToday onNavigateToContact={(cid) => navigate(`/contact?id=${cid}`)} />

      {/* Compact stat strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <button
            key={label}
            onClick={() => navigate('/contacts')}
            className="bg-white border border-[#E5E7EB] rounded-sm px-3 py-2 hover:border-[#0B5394] hover:shadow-sm transition-all text-left flex items-center gap-2.5"
            data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Icon size={18} weight="bold" style={{ color }} className="shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] font-semibold text-[#4B5563] uppercase tracking-wide leading-tight truncate">{label}</div>
              <div className="text-lg font-black text-[#111827] leading-none mt-0.5">{value.toLocaleString()}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Activation Funnel (admin only) */}
      {user?.role === 'admin' && funnel && (
        <ActivationFunnelWidget funnel={funnel} />
      )}

      {/* Demo Account Health (admin only) — spots prod/preview password drift instantly */}
      {user?.role === 'admin' && <DemoAccountHealthWidget />}

      {/* One-click login links for AI agents / VAs (admin only) */}
      {user?.role === 'admin' && <MagicLinkWidget />}

      {/* Crunchy's captured sales leads (admin only) */}
      {user?.role === 'admin' && <SalesLeadsWidget />}

      {/* Objection Memory — AI analysis of prospect pushback (admin only) */}
      {user?.role === 'admin' && <ObjectionMemoryWidget />}

      {/* Pipeline Funnel */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm mb-4" data-testid="pipeline-funnel-widget">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#111827] uppercase tracking-wide flex items-center gap-1.5">
            <FunnelSimple size={16} weight="bold" className="text-[#002FA7]" />
            Sales Pipeline
          </h3>
          <button
            onClick={() => navigate('/pipeline')}
            className="text-xs font-medium text-[#002FA7] hover:underline"
            data-testid="view-pipeline-link"
          >
            View Board
          </button>
        </div>
        <div className="p-5">
          <PipelineFunnel summary={pipelineSummary} onNavigate={navigate} />
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Companies */}
        <div className="bg-white border border-[#E5E7EB] rounded-sm" data-testid="top-companies-widget">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h3 className="text-sm font-semibold text-[#111827] uppercase tracking-wide">Top Companies</h3>
          </div>
          <div className="p-4">
            {stats?.top_companies?.length > 0 ? (
              <div className="space-y-2">
                {stats.top_companies.map(({ company, count }) => (
                  <div key={company} className="flex items-center justify-between py-1.5 border-b border-[#E5E7EB] last:border-0">
                    <span className="text-sm font-medium text-[#111827]">{company}</span>
                    <span className="text-sm font-bold text-[#002FA7]">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#4B5563] text-center py-6">No companies yet. Add contacts to see data here.</p>
            )}
          </div>
        </div>

        {/* Top Tags */}
        <div className="bg-white border border-[#E5E7EB] rounded-sm" data-testid="top-tags-widget">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h3 className="text-sm font-semibold text-[#111827] uppercase tracking-wide">Top Tags</h3>
          </div>
          <div className="p-4">
            {stats?.top_tags?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {stats.top_tags.map(({ tag, count }) => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#F3F4F6] border border-[#E5E7EB] text-xs font-medium text-[#111827] rounded-sm">
                    {tag} <span className="text-[#002FA7] font-bold">{count}</span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#4B5563] text-center py-6">No tags yet. Tag your contacts to see data here.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
